import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Enquiries from './pages/Enquiries';
import EnquiryForm from './pages/EnquiryForm';
import EnquiryDetail from './pages/EnquiryDetail';
import Analytics from './pages/Analytics';
import Clients from './pages/Clients';
import ClientForm from './pages/ClientForm';
import ClientPrint from './pages/ClientPrint';
import Transactions from './pages/Transactions';
import TransactionForm from './pages/TransactionForm';
import Admin from './pages/Admin';
import Roles from './pages/Roles';
import Hierarchy from './pages/Hierarchy';
import Permissions from './pages/Permissions';
import PermissionForm from './pages/PermissionForm';
import UserHierarchy from './pages/UserHierarchy';
import UserHierarchyForm from './pages/UserHierarchyForm';
import FormConfigs from './pages/FormConfigs';
import FormConfigForm from './pages/FormConfigForm';
import AdminKeyRotation from './pages/AdminKeyRotation';
import Services from './pages/Services';
import './styles/App.css';

function App() {
  // Use basename only in production (when deployed to XAMPP)
  // In development (npm start), use root path
  const basename = '/';

  return (
    <AuthProvider>
      <Router 
        basename={basename}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route element={<Layout />}>
            <Route path="/enquiries" element={
              <ProtectedRoute permission="enquiries.read">
                <Enquiries />
              </ProtectedRoute>
            } />

            <Route path="/enquiries/new" element={
              <ProtectedRoute permission="enquiries.create">
                <EnquiryForm />
              </ProtectedRoute>
            } />

            <Route path="/enquiries/edit/:id" element={
              <ProtectedRoute permission="enquiries.update">
                <EnquiryForm />
              </ProtectedRoute>
            } />

            <Route path="/enquiries/view/:id" element={
              <ProtectedRoute permission="enquiries.read">
                <EnquiryDetail />
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute permission="analytics.read">
                <Analytics />
              </ProtectedRoute>
            } />
            
            <Route path="/clients" element={
              <ProtectedRoute permission="clients.read">
                <Clients />
              </ProtectedRoute>
            } />
            <Route path="/clients/print/:id" element={
              <ProtectedRoute permission="clients.read">
                <ClientPrint />
              </ProtectedRoute>
            } />

            <Route path="/clients/new" element={
              <ProtectedRoute permission="clients.create">
                <ClientForm />
              </ProtectedRoute>
            } />

            <Route path="/clients/edit/:id" element={
              <ProtectedRoute permission="clients.update">
                <ClientForm />
              </ProtectedRoute>
            } />
            
            <Route path="/transactions" element={
              <ProtectedRoute permission="transactions.read">
                <Transactions />
              </ProtectedRoute>
            } />

            <Route path="/transactions/new" element={
              <ProtectedRoute permission="transactions.create">
                <TransactionForm />
              </ProtectedRoute>
            } />

            <Route path="/transactions/edit/:id" element={
              <ProtectedRoute permission="transactions.update">
                <TransactionForm />
              </ProtectedRoute>
            } />
            
            <Route path="/hierarchy" element={
              <ProtectedRoute permission="users.manage_hierarchy">
                <Hierarchy />
              </ProtectedRoute>
            } />
            
            <Route path="/roles" element={
              <ProtectedRoute requireAdmin>
                <Roles />
              </ProtectedRoute>
            } />

            <Route path="/permissions" element={
              <ProtectedRoute requireAdmin>
                <Permissions />
              </ProtectedRoute>
            } />

            <Route path="/permissions/new" element={
              <ProtectedRoute requireAdmin>
                <PermissionForm />
              </ProtectedRoute>
            } />

            <Route path="/permissions/edit/:id" element={
              <ProtectedRoute requireAdmin>
                <PermissionForm />
              </ProtectedRoute>
            } />

            <Route path="/user-hierarchy" element={
              <ProtectedRoute requireAdmin>
                <UserHierarchy />
              </ProtectedRoute>
            } />

            <Route path="/user-hierarchy/new" element={
              <ProtectedRoute requireAdmin>
                <UserHierarchyForm />
              </ProtectedRoute>
            } />

            <Route path="/form-configs" element={
              <ProtectedRoute requireAdmin>
                <FormConfigs />
              </ProtectedRoute>
            } />

            <Route path="/form-configs/new" element={
              <ProtectedRoute requireAdmin>
                <FormConfigForm />
              </ProtectedRoute>
            } />

            <Route path="/form-configs/edit/:id" element={
              <ProtectedRoute requireAdmin>
                <FormConfigForm />
              </ProtectedRoute>
            } />

            <Route path="/key-rotation" element={
              <ProtectedRoute requireAdmin>
                <AdminKeyRotation />
              </ProtectedRoute>
            } />

            <Route path="/services" element={
              <ProtectedRoute requireAdmin>
                <Services />
              </ProtectedRoute>
            } />
            
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            } />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/enquiries" replace />} />
          <Route path="*" element={<Navigate to="/enquiries" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

